import { FunctionComponent, MouseEvent, useState } from "react";

import { faCheck, faCopy } from "@fortawesome/pro-regular-svg-icons";
import { faSparkles } from "@fortawesome/pro-solid-svg-icons";

import { Icon } from "@/components";

import {
	LeadScoringBreakdown,
	LeadScoringCriterionBreakdown,
} from "@/pages/Private/pages/Apollo/schema/lead-scoring-breakdown.schema";

import { copyText } from "@/utils/copyText";

import { pointsBadgeClasses, prioBadgeClasses } from "./scoreTier";

/**
 * The caveat a C-tier score carries: it was derived from the company alone.
 *
 * Shared by every surface that renders a company-only badge (both list ladders,
 * the contacts company column, the connections table, the tracker editor) — the
 * same sentence used to be re-typed at each one, and two of them had drifted to
 * a shorter version.
 */
export const COMPANY_ONLY_SCORE_NOTE =
	"Score is based on company information only. No persona information is available or lead scoring has not yet finished.";

/**
 * Everything the card reads off a scored result. Kept structural rather than
 * tied to `PersonaCategoryResults` so the onboarding tour's test results — a
 * different schema carrying the same two fields — can render the same card
 * instead of hand-rolling a plain-text hover.
 */
export interface LeadScoringSource {
	leadScoringBreakdown?: LeadScoringBreakdown | null;
	overallReasoning?: string | null;
}

interface LeadScoringPopoverProps {
	scoring: LeadScoringSource;
	/** Resolved pill label (P1 / P2 / P3 / C1 / C2 / C3 / DQ / ?) shown in the header. */
	scoreLabel: string;
	/**
	 * A caveat about the score as a whole, shown once under the header — currently
	 * "company information only" for a C-tier lead. It used to be concatenated
	 * onto the reasoning as an `<em>` tag, which put a statement about the score
	 * inside the explanation of one criterion.
	 */
	note?: string;
	onCopy?: (value: string) => void;
}

const stop = (e: MouseEvent) => {
	e.preventDefault();
	e.stopPropagation();
};

const CriterionRow: FunctionComponent<{
	criterion: LeadScoringCriterionBreakdown;
	onCopy?: (value: string) => void;
}> = ({ criterion, onCopy }) => {
	const [copied, setCopied] = useState(false);

	const copy = (e: MouseEvent) => {
		stop(e);
		void copyText(criterion.reasoning).then((copied) => {
			if (!copied) {
				return;
			}

			onCopy?.(criterion.reasoning);
			setCopied(true);
			setTimeout(() => setCopied(false), 1000);
		});
	};

	return (
		<div
			className={`group/val cursor-pointer rounded-lg px-2 py-1.5 transition-colors ${
				copied ? "bg-status-success-light" : "hover:bg-gray-50"
			}`}
			onClick={copy}
		>
			<div className="mb-1 flex items-center justify-between gap-2">
				<span className="text-xs font-bold text-gray-900">{criterion.label}</span>
				<span
					className={`flex h-5 shrink-0 items-center rounded-md px-2 text-[10.5px] font-extrabold ${pointsBadgeClasses(
						criterion.score
					)}`}
				>
					{criterion.score} {criterion.score === 1 ? "point" : "points"}
				</span>
			</div>
			<div className="flex items-start gap-2">
				<span className="flex-1 whitespace-normal text-[11.5px] leading-snug text-text-meta">
					{criterion.reasoning}
				</span>
				<Icon
					className={`mt-0.5 h-[14px] w-[14px] shrink-0 transition-opacity ${
						copied
							? "text-status-success opacity-100"
							: "text-text-meta opacity-0 group-hover/val:opacity-100"
					}`}
					icon={copied ? faCheck : faCopy}
				/>
			</div>
		</div>
	);
};

const ScoringSection: FunctionComponent<{
	title: string;
	criteria: LeadScoringCriterionBreakdown[];
	/** The disqualifying section is a verdict, not a category — it reads as an error. */
	tone?: "category" | "error";
	onCopy?: (value: string) => void;
}> = ({ title, criteria, tone = "category", onCopy }) => {
	if (criteria.length === 0) {
		return null;
	}

	return (
		<>
			<div
				className={`px-2 pb-1 pt-2 text-[10px] font-bold uppercase tracking-wide ${
					tone === "error" ? "text-status-error" : "text-blue-action"
				}`}
			>
				{title}
			</div>
			{criteria.map((criterion) => (
				<CriterionRow key={criterion.label} criterion={criterion} onCopy={onCopy} />
			))}
		</>
	);
};

const hasBreakdown = (breakdown?: LeadScoringBreakdown | null): breakdown is LeadScoringBreakdown =>
	!!breakdown && (breakdown.company.length > 0 || breakdown.persona.length > 0);

/**
 * The criteria that disqualified the lead — asked for only once the lead IS
 * disqualified, since `score` alone cannot answer it.
 *
 * The API clamps points to >= 0, so a criterion that matched the rubric's
 * "Disqualified" bucket (a real 0) and one whose value merely could not be
 * identified (-1, ignored per its "If not found") both arrive as 0. `score === 0`
 * would therefore accuse an ignored criterion of rejecting a lead it passed. The
 * engine flags the real ones; the 0 is only a fallback for rows scored before
 * that flag existed.
 */
const disqualifyingCriteria = (
	breakdown: LeadScoringBreakdown
): LeadScoringCriterionBreakdown[] => {
	const all = [...breakdown.company, ...breakdown.persona];
	const flagged = all.filter((criterion) => criterion.disqualifying);

	return flagged.length > 0 ? flagged : all.filter((criterion) => criterion.score === 0);
};

/**
 * What the card lists.
 *
 * A disqualified lead shows ONLY what disqualified it. One 0 rejects the lead
 * however well everything else scored, so the surviving criteria are noise — and
 * listing them in rubric order is what used to bury the reason below the scroll
 * (a real lead read as "DQ" over three green criteria and a "13/15" footer, its
 * Job Level 0 three scrolls down).
 */
const ScoringBody: FunctionComponent<{
	breakdown: LeadScoringBreakdown;
	disqualified: LeadScoringCriterionBreakdown[];
	onCopy?: (value: string) => void;
}> = ({ breakdown, disqualified, onCopy }) =>
	disqualified.length > 0 ? (
		<ScoringSection criteria={disqualified} title="Disqualified by" tone="error" onCopy={onCopy} />
	) : (
		<>
			<ScoringSection criteria={breakdown.company} title="Company" onCopy={onCopy} />
			<ScoringSection criteria={breakdown.persona} title="Persona" onCopy={onCopy} />
		</>
	);

/** The card's header: the "Lead scoring" title and the resolved prio badge. */
const CardHeader: FunctionComponent<{ scoreLabel: string }> = ({ scoreLabel }) => (
	<div className="flex items-center gap-1.5 border-b border-gray-200 px-3 py-2.5 text-[10px] font-bold uppercase tracking-wide text-text-meta">
		<Icon className="h-[12px] w-[12px]" icon={faSparkles} />
		Lead scoring
		<span
			className={`ml-auto rounded-md px-2 py-0.5 text-[10px] font-extrabold ${prioBadgeClasses(
				scoreLabel
			)}`}
		>
			{scoreLabel}
		</span>
	</div>
);

/** The score-level caveat, directly under the header so it frames what follows. */
const CardNote: FunctionComponent<{ note?: string }> = ({ note }) =>
	note ? (
		<div className="border-b border-gray-100 px-3 py-2 text-[11px] leading-snug text-text-meta">
			{note}
		</div>
	) : null;

/**
 * The legacy `overallReasoning` HTML, shown when a result carries no structured
 * breakdown: rows scored before the field existed, and every verdict the engine
 * reaches *before* it scores a criterion — a disqualifying company category, a
 * persona the classifier could not match. Those leads have no criteria to list,
 * but that is the only part of the card they are missing, so they keep the frame
 * (PREC-3499 follow-up). Rendering them as a bare tooltip instead made a DQ lead
 * look like a different feature from the P1 lead one row above it.
 */
const LegacyReasoning: FunctionComponent<{ className: string; html: string }> = ({
	className,
	html,
}) => (
	// eslint-disable-next-line @eslint-react/dom/no-dangerously-set-innerhtml
	<div className={className} dangerouslySetInnerHTML={{ __html: html }} />
);

/** Footer label, shared by both variants' overall-score row. */
const OverallScoreLabel: FunctionComponent = () => (
	<span className="text-[11px] font-bold uppercase tracking-wide text-text-meta">
		Overall score
	</span>
);

/** Whether a scoring result has anything the card can show (breakdown or legacy HTML). */
export const hasLeadScoringContent = (scoring?: LeadScoringSource | null): boolean =>
	!!scoring && (hasBreakdown(scoring.leadScoringBreakdown) || !!scoring.overallReasoning);

/**
 * Beautified Lead Scoring hover (PREC-3499): a structured card with a sticky
 * header (prio badge), scrollable Company/Persona criterion rows with
 * colour-coded sub-scores and click-to-copy reasoning, and a sticky footer with
 * the overall numeric score. Falls back to the legacy `overallReasoning` HTML
 * when no structured breakdown is available (results scored before the field
 * existed). Tailwind-only so it is safe inside the extension shadow root.
 */
export const LeadScoringPopover: FunctionComponent<LeadScoringPopoverProps> = ({
	scoring,
	scoreLabel,
	note,
	onCopy,
}) => {
	const breakdown = scoring.leadScoringBreakdown;

	if (!hasBreakdown(breakdown)) {
		if (!scoring.overallReasoning) {
			return null;
		}

		return (
			<div className="flex w-[320px] flex-col">
				<CardHeader scoreLabel={scoreLabel} />
				<CardNote note={note} />
				<div className="max-h-[300px] overflow-y-auto px-3 py-2.5">
					<LegacyReasoning
						className="whitespace-normal text-[12.5px] leading-snug text-text-meta"
						html={scoring.overallReasoning}
					/>
				</div>
				{/* Only a DQ verdict has a total worth naming here — for any other
				    label the header badge already says everything the row knows. */}
				{scoreLabel === "DQ" && (
					<div className="flex items-center justify-between border-t border-gray-200 px-3.5 py-2.5">
						<OverallScoreLabel />
						<span className="text-sm font-extrabold text-status-error">Disqualified</span>
					</div>
				)}
			</div>
		);
	}

	// Only a disqualified lead has disqualifying criteria. Asking otherwise would
	// let a merely-unidentified criterion (clamped to 0) hijack a lead that passed.
	const disqualified = scoreLabel === "DQ" ? disqualifyingCriteria(breakdown) : [];

	return (
		<div className="flex w-[320px] flex-col">
			<CardHeader scoreLabel={scoreLabel} />
			<CardNote note={note} />
			<div className="max-h-[300px] overflow-y-auto p-1.5">
				<ScoringBody breakdown={breakdown} disqualified={disqualified} onCopy={onCopy} />
			</div>
			<div className="flex items-center justify-between border-t border-gray-200 px-3.5 py-2.5">
				<OverallScoreLabel />
				{/* A disqualified lead has no meaningful total — reporting one reads as a
				    recommendation ("13/15") for a lead the scoring just rejected. */}
				{disqualified.length > 0 ? (
					<span className="text-sm font-extrabold text-status-error">Disqualified</span>
				) : (
					<span className="text-sm font-extrabold text-blue-action">
						{breakdown.overall.score}/{breakdown.overall.max}
					</span>
				)}
			</div>
		</div>
	);
};

/**
 * Inline variant of the scoring breakdown for detail views (PREC-3601): rebuilt
 * to live *inside* the collapsible "Lead Scoring Reasoning" section, so it has
 * no own frame, no duplicate "Lead scoring" header, and no inner scroll cap —
 * the criteria flow at full height and the detail panel itself scrolls. The
 * prio badge moves into the overall-score footer row. Falls back to the legacy
 * `overallReasoning` HTML when no structured breakdown exists.
 */
export const LeadScoringInline: FunctionComponent<LeadScoringPopoverProps> = ({
	scoring,
	scoreLabel,
	note,
	onCopy,
}) => {
	const breakdown = scoring.leadScoringBreakdown;

	if (!hasBreakdown(breakdown)) {
		if (!scoring.overallReasoning) {
			return null;
		}

		return (
			<div className="flex w-full flex-col">
				<CardNote note={note} />
				<LegacyReasoning
					className="whitespace-normal px-2 text-sm leading-5 text-text-meta"
					html={scoring.overallReasoning}
				/>
				<div className="mt-2 flex items-center justify-between border-t border-gray-200 px-2 pt-2.5">
					<OverallScoreLabel />
					<span
						className={`rounded-md px-2 py-0.5 text-[10px] font-extrabold ${prioBadgeClasses(
							scoreLabel
						)}`}
					>
						{scoreLabel}
					</span>
				</div>
			</div>
		);
	}

	// Only a disqualified lead has disqualifying criteria. Asking otherwise would
	// let a merely-unidentified criterion (clamped to 0) hijack a lead that passed.
	const disqualified = scoreLabel === "DQ" ? disqualifyingCriteria(breakdown) : [];

	return (
		<div className="flex w-full flex-col">
			<CardNote note={note} />
			<ScoringBody breakdown={breakdown} disqualified={disqualified} onCopy={onCopy} />
			<div className="mt-2 flex items-center justify-between border-t border-gray-200 px-2 pt-2.5">
				<OverallScoreLabel />
				<span className="flex items-center gap-2">
					<span
						className={`rounded-md px-2 py-0.5 text-[10px] font-extrabold ${prioBadgeClasses(
							scoreLabel
						)}`}
					>
						{scoreLabel}
					</span>
					{disqualified.length === 0 && (
						<span className="text-sm font-extrabold text-blue-action">
							{breakdown.overall.score}/{breakdown.overall.max}
						</span>
					)}
				</span>
			</div>
		</div>
	);
};
